import $ from 'jQuery';
import style from './machine_app_item_editor.css';
import environment from '../environment';
import helpers from '../helpers';
import machine_app_implementation_loader from './models/machine_app_implementation_loader';
import machine_app_state from './models/machine_app_state';
import ml_widgets from '../legacy/widgets';
import { greenCheckbox } from './components/machine_app_green_checkbox';
import { modalMessageBox } from './components/machine_app_modal_toast_message_box';
import { progressBar } from './components/machine_app_progressbar';
import { showConfirmationModal } from './components/machine_app_confirmation_modal';

/**
 * Renders the item editor modal for selected item
 * @param {Object} pItem
 * @param {Function} pOnUpdate
 * @param {String} [pDefaultStep]
 */
export function machineAppItemEditor(pItem, pOnUpdate, pDefaultStep = undefined) {
  const lModalHeader = $('<div>')
    .addClass('machineapp_item_editor_header_container');
  const lModalBody = $('<div>')
    .addClass('machineapp_item_editor_body_container');
  const lModalContent = $('<div>')
    .addClass('machineapp_item_modal_content');
  const lModalLeftPaneBody = $('<div>')
    .addClass('machineapp_item_modal_container machineapp_item_modal_container_left');
  const lModalRightPaneBody = $('<div>')
    .addClass('machineapp_item_modal_container machineapp_item_modal_container_right');

  let itemEditorModal;
  let itemEditor;
  const modalOnClose = (pForce) => {
    const lOnExit = () => {
      itemEditorModal?.hide();
      machine_app_state.getInterface().cleanupCalibratorManipulators();
      pOnUpdate && pOnUpdate();
    };

    if (itemEditor && !pForce) {
      itemEditor.controls.requestClose(lOnExit);
    }
    else {
      lOnExit();
    }
  };

  getItemEditor({
    pItem: pItem,
    pCloseModal: (pForce) => modalOnClose(pForce),
    pOnUpdate: pOnUpdate,
    pDefaultStep: pDefaultStep,
    pCanReset: pItem.getItemType() === 'calibration',
    onReady: (inItemEditor) => {
      if (!inItemEditor) {
        modalOnClose(true);
        return;
      }

      itemEditor = inItemEditor;
      lModalHeader.empty().append(
        itemEditor.widget.header
      );

      lModalBody.empty().append(
        lModalContent.empty().append(
          lModalLeftPaneBody.empty().append(
            itemEditor.widget.leftPane
          ),
          lModalRightPaneBody.empty().append(
            itemEditor.widget.rightPane
          )
        )
      );

      itemEditorModal = machineAppItemModal({
        header: lModalHeader,
        body: lModalBody,
        pExitModal: (pForce) => modalOnClose(pForce)
      });

      itemEditorModal.widget.appendTo(environment.isOnCad() ? `#pane_machine_logic` : 'body');
    }
  });
}

/**
 * @typedef {Object} WidgetReturn
 * @property {JQuery} header
 * @property {JQuery} leftPane
 * @property {JQuery} rightPane
 */

/**
 * @typedef {Object} ItemEditorContainerProps
 * @property {Object} pItem MachineAppParameters | MachineAppGlobalAsset | MachineAppConfiguration
 * @property {Function} pCloseModal
 * @property {Function} pOnUpdate
 * @property {String} pDefaultStep
 * @property {Boolean} pCanReset
 * @property {({ widget: WidgetReturn; controls: { requestClose: () => void }; }) => void} onReady
 */

/**
 *
 * @param {ItemEditorContainerProps} ItemEditorContainerProps
 * @returns {{widget: {header: JQuery, leftPane: JQuery, rightPane: JQuery}, controls: {requestClose: Function}}}
 */
function getItemEditor({
  pItem,
  pCloseModal,
  pOnUpdate,
  pDefaultStep,
  pCanReset,
  onReady
}) {
  const lFlavorText = getFlavorText(pItem, pCanReset);
  const lHeaderContainer = $('<h5>')
    .addClass('machineapp_item_editor_header_container_title')
    .text(lFlavorText.headerText);
  const lSidebarContainer = $('<div>')
    .addClass('machineapp_editor_left_pane_container');
  const lRightPaneContainer = $('<div>')
    .addClass('machineapp_editor_right_pane_container');
  let lRightPaneContent;
  const lProgressBar = progressBar({ pInitialStep: 1, pIsPercentage: true });
  const lStepWrapper = $('<div>').addClass('machineapp_step_wrapper').addClass('ml_scrollable');
  const lUpdateStepper = (pStepDisplayList, pPassedParent) => {
    let lStepsCompleted = 0;
    let lOmittedFromProgressCounter = 0;
    let lStepperContainer;
    const lChildList = $('<ul>').addClass('machineapp_step_list');

    if(!pPassedParent){
      lStepperContainer = lStepWrapper;
      lStepperContainer.children().remove();
    }
    else {
      lStepperContainer = pPassedParent;
    }

    lProgressBar.controls.resetProgress();

    pStepDisplayList.forEach(function(pStep) {
      const lChild = $('<li>').addClass('machineapp_left_pane_step').on('click', pStep.onClick);
      const lCheckbox = greenCheckbox();
      const lName = $('<label>').text(pStep.title);

      if (pPassedParent){
        lChild.addClass('machineapp_left_pane_substep');
      }
      lChildList.append(lChild);

      lChild.append(lCheckbox.widget, lName);

      if (pStep.disabled) {
        lChild.addClass('machineapp_left_pane_step_disabled');
      }
      if (pStep.isSelected) {
        lChild.addClass('machineapp_left_pane_step_selected');
      }
      if (pStep.isComplete) {
        lCheckbox.controls.check();

        if (!pStep.isOmittedFromProgress) {
          lStepsCompleted++;
        }
      }
      if (pStep.isOmittedFromProgress) {
        lOmittedFromProgressCounter++;
      }
      if (pStep.isSelected && pStep.stepList?.length > 0){
        lUpdateStepper(pStep.stepList, lChildList);
      }
    });

    lStepperContainer.append(lChildList);
    const lTotalSteps = pStepDisplayList.length - lOmittedFromProgressCounter;

    lProgressBar.controls.updateProgress(lStepsCompleted, lTotalSteps);
    machine_app_state.getInterface().cleanupCalibratorManipulators();
    return lStepperContainer;
  };

  // Build a confirmation modal for an existing modal. To be called on clicking X
  const lClose = pCanCloseModalCallback => {
    if (lUnsavedData || lUnsavedName) {
      showConfirmationModal({
        pTitle: 'You have unsaved progress.',
        pSubTitle: 'Would you like to save your changes?',
        pCancelText: 'Discard & Exit',
        pOnCancel: () => pCanCloseModalCallback(), // pOnCancel means 'Discard and exit'
        pConfirmText: 'Save & Exit',
        pOnConfirm: () => lSave((pSuccess) => {pSuccess && pCanCloseModalCallback();}),
        pOnXClicked: () => {}, // pOnXClicked means 'Cancel and stay'
        pConfirmBtnTheme: 'success',
        pCancelBtnTheme: 'danger'
      });
    }
    else {
      pCanCloseModalCallback();
    }
  };

  const returnValue = {
    widget: {
      header: lHeaderContainer,
      leftPane: lSidebarContainer,
      rightPane: lRightPaneContainer,
    },
    controls: {
      requestClose : lClose
    }
  };

  let lUnsavedData = undefined;
  let lUnsavedName = undefined;
  const lSave = (pOnSaveComplete = (pSuccess) => pSuccess) => {
    if (!lUnsavedData && !lUnsavedName) {
      pOnUpdate();
      pOnSaveComplete(true);

      return;
    }

    const lSaveDataPromises = [];
    if (lUnsavedData) {
      lSaveDataPromises.push(
        new Promise(resolve => pItem.setData(helpers.simpleClone(lUnsavedData), resolve))
          .then(pSuccess => {
            if (pSuccess) {
              lUnsavedData = undefined;
            }

            return pSuccess;
          })
      );
    }

    if(lUnsavedName) {
      lSaveDataPromises.push(
        new Promise(resolve => pItem.setName(lUnsavedName, resolve))
          .then(pSuccess => {
            if (pSuccess) {
              lUnsavedName = undefined;
            }

            return pSuccess;
          })
      );
    }

    Promise.all(lSaveDataPromises)
      .then(pSuccesses => {
        const allSucceeded = pSuccesses.every(pSuccess => pSuccess);
        if (!allSucceeded) {
          modalMessageBox(lFlavorText.saveFail, 'error');
        }

        pOnUpdate();
        pOnSaveComplete(allSucceeded);
      });
  };

  const lRender = function(pSelectedStep, isFirstRender = false) {
    const lSetDirty = (pData) => {
      if (helpers.areJsValuesEqual(pItem.getData(), pData)) {
        lUnsavedData = undefined;
      }
      else {
        lUnsavedData = pData;
      }
    };

    let lResetButton = undefined;
    // Reset Button
    if (pCanReset) {
      lResetButton = $('<button>')
        .addClass('machine_item_app_reset_button')
        .text(lFlavorText.resetButtonText)
        .on('click', function() {
          showConfirmationModal({
            pTitle: 'Are you sure that you want to reset?',
            pSubTitle: lFlavorText.resetConfirmationSubTitle,
            pOnConfirm: function() {
              pItem.setData(undefined);
              lUnsavedData = undefined;
              lRender();
              pOnUpdate();
            },
            pCancelText: 'Cancel',
            pConfirmText: 'Reset',
          });
        });
    }

    const renderSidebarContainer = () => {
      const lNameContainer = $('<div>')
        .addClass('machineapp_editor_left_pane_fieldset_container v_layout');
      const lNameLabel = $('<label>')
        .text(lFlavorText.nameInput);
      const lNameOnValue = (pName) => {
        if (pItem.getName() === pName) {
          lUnsavedData = undefined;
        }
        else {
          lUnsavedName = pName;
        }
      };

      const lNameInput = ml_widgets.stringWidget(null, lFlavorText.nameInput, pItem.getName(), lNameOnValue);
      lSidebarContainer.empty().append(
        lNameContainer.append(
          lNameLabel,
          lNameInput
        ),
        lResetButton,
        lProgressBar.widget,
        lStepWrapper
      );
    };
    renderSidebarContainer();

    const lShowInterface = {
      data: helpers.simpleClone(pItem.getData()),
      type: pItem.getType(),
      updateStepper: lUpdateStepper,
      setDirty: lSetDirty,
      save: lSave,
      close: pCloseModal,
      defaultStep: pSelectedStep,
    };

    // TODO: Support asynchronous rendering for configuration and calibration
    switch (pItem.getItemType()) {
      case 'configuration':
        lRightPaneContent = machine_app_implementation_loader.instance.createConfigurator(lShowInterface);
        lRightPaneContainer.empty().append(
          lRightPaneContent
        );
        if (isFirstRender) onReady(returnValue);
        break;
      case 'calibration':
        lRightPaneContent = machine_app_implementation_loader.instance.createMachineParameters(lShowInterface);
        lRightPaneContainer.empty().append(
          lRightPaneContent
        );
        if (isFirstRender) onReady(returnValue);
        break;
      case 'globalAsset':
        lShowInterface.globalAsset = pItem;
        lShowInterface.assetType = pItem.getAssetType();
        machine_app_implementation_loader.instance.createGlobalAsset(lShowInterface)
          .then(rightPaneContent => {
            lRightPaneContent = rightPaneContent;
            lRightPaneContainer.empty().append(
              lRightPaneContent
            );
            renderSidebarContainer(); // We give the option to change sidebar data through .globalAsset. Might need to re-render
            if (isFirstRender) onReady(returnValue);
          })
          .catch(() => {
            if (isFirstRender) onReady();
          });
        break;
      default:
        return;
    }
  };

  lRender(pDefaultStep, true);
}

function getFlavorText(pItem) {
  const allFlavorText = machine_app_implementation_loader.instance.getFlavorText(pItem.getType());
  let lAppStrings;

  switch (pItem.getItemType()) {
    case 'configuration':
      lAppStrings = allFlavorText.configuration;
      break;
    case 'calibration':
      lAppStrings = allFlavorText.machineParameters;
      break;
    case 'globalAsset':
    {
      const flavorTextMap = allFlavorText.globalAssetFlavorTextMap;
      lAppStrings = flavorTextMap[pItem.getAssetType()] ?? flavorTextMap.default;
      break;
    }
    default:
      console.error('Attempting to show item view for invalid item: ', pItem);
      break;
  }

  return {
    nameInput: `${lAppStrings.defaultName} Name`,
    saveFail: `Failed to save ${lAppStrings.defaultName.toLowerCase()}`,
    headerText: `Create ${lAppStrings.defaultName}`,
    resetButtonText: `Reset ${lAppStrings.defaultName}`,
    resetConfirmationSubTitle: `This will erase all data from this ${lAppStrings.singularName}`
  };
}

//TODO: clean up this modal so that it doesn't rely on machineLogic styles
function machineAppItemModal({ header, body, pExitModal } = {}) {
  const lContainer = $('<div>')
    .addClass('ml_modal_container');
  const lModal = $('<div>')
    .addClass('machineapp_modal');
  const lHeaderContainer = $('<div>')
    .addClass('machineapp_modal_header');
  const lBodyContainer = $('<div>')
    .addClass('ml_modal_body');

  const lExitButton = $('<button>')
    .addClass('far machineapp_modal_close')
    .on('click', () => pExitModal());

  const lRender = function() {
    lContainer.append(
      lModal.empty().append(
        lHeaderContainer.empty().append(
          header,
          lExitButton
        ),
        lBodyContainer.empty().append(
          body
        ),
      )
    );
  };

  lRender();

  if (environment.isOnCad()) {
    $('#tabs_applications').one('click', () => pExitModal(true));
    lModal.css('transform', 'scale(0.9)').css('width', '100%'); // TODO: This is hacky way to scale the modal on the CAD
  }

  return {
    widget    : lContainer,
    hide      : () => lContainer.remove(),
    render    : lRender
  };
}
